import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Phone, Mail, Star, History, TrendingUp, Utensils, Gift, Tag, ArrowLeft, QrCode, Hash, Award } from 'lucide-react';
import PremiumDatePicker from '../../components/PremiumDatePicker';
import { QRCodeSVG } from 'qrcode.react';
import { calculateLoyaltyLevel, defaultLoyaltyLevels } from '../../utils/loyalty';

const HistoryTabs = ({ history, customer, discounts, primaryColor, tenant, t }) => {
    const [activeTab, setActiveTab] = useState('orders');

    const usedRewards = (customer.activeRewards || []).filter(r => r.status === 'used');

    return (
        <>
            {/* Tab Headers */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '1.25rem', background: 'var(--bg-main)', borderRadius: '14px', padding: '4px', border: '1px solid var(--border)' }}>
                <button
                    onClick={() => setActiveTab('orders')}
                    style={{
                        flex: 1, padding: '10px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                        fontSize: '0.8125rem', fontWeight: 800,
                        background: activeTab === 'orders' ? primaryColor : 'transparent',
                        color: activeTab === 'orders' ? '#fff' : 'var(--text-muted)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                        transition: 'all 0.2s ease'
                    }}
                >
                    <History size={15} />
                    {(tenant?.plan === 'pro' || tenant?.plan === 'premium') ? t('portal.orderHistory') : t('portal.pointHistory')}
                </button>
                <button
                    onClick={() => setActiveTab('rewards')}
                    style={{
                        flex: 1, padding: '10px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                        fontSize: '0.8125rem', fontWeight: 800,
                        background: activeTab === 'rewards' ? primaryColor : 'transparent',
                        color: activeTab === 'rewards' ? '#fff' : 'var(--text-muted)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                        transition: 'all 0.2s ease'
                    }}
                >
                    <Award size={15} />
                    Ödül Geçmişi
                    {usedRewards.length > 0 && (
                        <span style={{ background: activeTab === 'rewards' ? 'rgba(255,255,255,0.3)' : primaryColor + '20', color: activeTab === 'rewards' ? '#fff' : primaryColor, fontSize: '0.6875rem', fontWeight: 900, padding: '1px 7px', borderRadius: '8px' }}>
                            {usedRewards.length}
                        </span>
                    )}
                </button>
            </div>

            {/* Orders Tab */}
            {activeTab === 'orders' && (
                <>
                    {history.length === 0 ? (
                        <div style={{ padding: '2rem', textAlign: 'center', opacity: 0.5 }}>{t('portal.noHistory')}</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            {history.slice(0, 15).map((item) => {
                                const isPurchase = item.type === 'Purchase';
                                const isReward = item.type === 'RewardClaim' || item.type === 'DiscountRedemption';
                                const interactionProducts = isPurchase ? item.items || [] : [];

                                const purchaseImages = isPurchase
                                    ? Array.from(new Set(interactionProducts.map(p => p.image).filter(Boolean)))
                                    : [];

                                let rewardImage = null;
                                if (isReward && item.discountId) {
                                    const d = (discounts || []).find(x => x.id === item.discountId);
                                    rewardImage = d?.imageUrl;
                                }

                                return (
                                    <div key={item.id} style={{
                                        display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px',
                                        background: '#ffffff', borderRadius: '20px', border: '1px solid #f1f5f9',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.02)', transition: 'transform 0.2s ease',
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                            <div style={{
                                                width: '56px', height: '56px', borderRadius: '16px', background: '#f8fafc',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                position: 'relative', flexShrink: 0, overflow: 'visible'
                                            }}>
                                                {isPurchase && purchaseImages.length > 0 ? (
                                                    purchaseImages.length === 1 ? (
                                                        <img src={purchaseImages[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '16px' }} />
                                                    ) : (
                                                        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                                                            {purchaseImages.slice(0, 3).map((img, i) => (
                                                                <div key={i} style={{
                                                                    position: 'absolute', top: i * 4 + 'px', left: i * 4 + 'px',
                                                                    width: '44px', height: '44px', borderRadius: '12px',
                                                                    border: '2px solid #ffffff', overflow: 'hidden',
                                                                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)', zIndex: 10 - i
                                                                }}>
                                                                    <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )
                                                ) : (isReward && rewardImage) ? (
                                                    <img src={rewardImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '16px' }} />
                                                ) : (
                                                    <div style={{ color: primaryColor }}>
                                                        {item.type === 'Visit' ? <TrendingUp size={24} /> :
                                                         isPurchase ? <Utensils size={24} /> :
                                                         <Gift size={24} />}
                                                    </div>
                                                )}
                                            </div>

                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{
                                                    fontSize: '0.9375rem', fontWeight: 800, color: '#1a1412',
                                                    lineHeight: 1.3, marginBottom: '4px', wordBreak: 'break-word'
                                                }}>
                                                    {isPurchase ? (
                                                        interactionProducts.length > 0
                                                            ? interactionProducts.map(p => `${p.name} (${p.quantity}x)`).join(', ')
                                                            : (item.content || 'Sipariş Detayı')
                                                    ) : item.type === 'Visit' ? (
                                                        'Ziyaret / QR Okutma'
                                                    ) : item.type === 'RewardClaim' ? (
                                                        'Ödül Kullanımı'
                                                    ) : item.type === 'DiscountRedemption' ? (
                                                        'Kampanya Kullanımı'
                                                    ) : (
                                                        item.content?.replace('Used Discount:', 'Kampanya:')
                                                            .replace('Reward Claimed:', 'Ödül:')
                                                            .replace('Ödül Teslim Alındı:', 'Ödül:')
                                                            .replace('Check-in processed', 'Ziyaret Girişi')
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>
                                                        {item.timestamp ? new Date(item.timestamp).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                                    </span>
                                                    {isPurchase && item.totalAmount !== undefined && (
                                                        <span style={{
                                                            fontSize: '0.75rem', background: primaryColor + '10',
                                                            color: primaryColor, padding: '2px 8px', borderRadius: '6px', fontWeight: 800
                                                        }}>
                                                            {item.totalAmount} TL
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div style={{ textAlign: 'right' }}>
                                                {item.type === 'Visit' && (
                                                    <div style={{ fontWeight: 900, color: '#22c55e', fontSize: '0.875rem' }}>+5 Puan</div>
                                                )}
                                                {isPurchase && item.earnedPoints > 0 && (
                                                    <div style={{ fontWeight: 900, color: '#22c55e', fontSize: '0.875rem' }}>+{item.earnedPoints} Puan</div>
                                                )}
                                                {item.type === 'RewardClaim' && (
                                                    <div style={{ fontWeight: 900, color: '#ef4444', fontSize: '0.8125rem' }}>-{item.pointsDeducted} Puan</div>
                                                )}
                                            </div>
                                        </div>

                                        {isPurchase && item.appliedPromotionTitle && (
                                            <div style={{
                                                marginLeft: '70px', fontSize: '0.75rem', color: primaryColor,
                                                fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px',
                                                padding: '6px 12px', background: primaryColor + '08', borderRadius: '8px', width: 'fit-content'
                                            }}>
                                                <Tag size={12} />
                                                {item.appliedPromotionTitle}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}

            {/* Rewards Tab */}
            {activeTab === 'rewards' && (
                <>
                    {usedRewards.length === 0 ? (
                        <div style={{ padding: '2rem', textAlign: 'center', opacity: 0.5 }}>Henüz kullanılmış ödül yok.</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {usedRewards.map((reward, idx) => (
                                <div key={reward.id || idx} style={{
                                    display: 'flex', alignItems: 'center', gap: '14px', padding: '16px',
                                    background: '#ffffff', borderRadius: '20px', border: '1px solid #f1f5f9',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
                                }}>
                                    <div style={{
                                        width: '56px', height: '56px', borderRadius: '16px',
                                        background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                        <Award size={24} color="#f59e0b" />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: '0.9375rem', fontWeight: 800, color: '#1a1412', marginBottom: '4px' }}>
                                            {reward.title || reward.rewardTitle || 'Ödül'}
                                        </div>
                                        {reward.code && (
                                            <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, fontFamily: 'monospace', marginBottom: '2px' }}>
                                                Kod: {reward.code}
                                            </div>
                                        )}
                                        <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>
                                            {reward.usedAt ? new Date(reward.usedAt).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                        </div>
                                    </div>
                                    <div style={{
                                        fontSize: '0.6875rem', fontWeight: 800, color: '#22c55e',
                                        background: '#f0fdf4', padding: '4px 10px', borderRadius: '8px',
                                    }}>
                                        Kullanıldı
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </>
    );
};

const CustomerPortalProfile = ({
    customer,
    setCustomer,
    isEditingProfile,
    setIsEditingProfile,
    handleUpdateProfile,
    paymentLoading,
    tenant,
    customerId,
    primaryColor,
    history,
    discounts
}) => {
    const { t } = useTranslation();

    return (
        <div className="fade-in">
            {!isEditingProfile ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {/* Profile Header Card */}
                    <div className="card glass-premium" style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, minWidth: 0 }}>
                            <div style={{ width: '60px', height: '60px', borderRadius: '20px', background: primaryColor + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', color: primaryColor, flexShrink: 0 }}>
                                <User size={30} />
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <h3 style={{ fontSize: '1.25rem', fontWeight: 900, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{customer.firstName || ''} {customer.lastName || ''}</h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                                    {customer.phone && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                                            <Phone size={14} style={{ flexShrink: 0 }} />
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{customer.phone}</span>
                                        </div>
                                    )}
                                    {customer.email && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                                            <Mail size={14} style={{ flexShrink: 0 }} />
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{customer.email}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsEditingProfile(true)}
                            style={{ background: 'var(--bg-main)', border: '1px solid var(--border)', color: 'var(--text-main)', padding: '10px 16px', borderRadius: '12px', fontSize: '0.8125rem', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, whiteSpace: 'nowrap' }}
                        >
                            Düzenle
                        </button>
                    </div>

                    {/* Loyalty Progress Card */}
                    {(() => {
                        const currentTier = calculateLoyaltyLevel(customer, tenant);
                        const allTiers = [...(tenant?.loyaltyLevels && tenant.loyaltyLevels.length > 0 ? tenant.loyaltyLevels : defaultLoyaltyLevels)].sort((a, b) => a.minPoints - b.minPoints);

                        // Find current and next
                        const currentTierIndex = allTiers.findIndex(t => t.label === currentTier?.label);
                        const nextTier = allTiers[currentTierIndex + 1];

                        if (!nextTier && !currentTier) return null;

                        const getLoyaltyStats = (tier) => {
                            const earnings = customer.pointEarnings || [];
                            const now = new Date();
                            const anchor = new Date(now);
                            anchor.setHours(4, 0, 0, 0);
                            if (now.getHours() < 4) anchor.setDate(anchor.getDate() - 1);

                            if (tier?.periodDays && tier.periodDays > 0) {
                                const cutoff = new Date(anchor.getTime() - (tier.periodDays * 24 * 60 * 60 * 1000));
                                const pts = earnings
                                    .filter(e => new Date(e.t) >= cutoff)
                                    .reduce((sum, e) => sum + (e.p || 0), 0);
                                return { points: pts, cutoffDate: cutoff };
                            }
                            // All time log integration
                            const pts = earnings.reduce((sum, e) => sum + (e.p || 0), 0);
                            return { points: pts, cutoffDate: null };
                        };

                        const stats = getLoyaltyStats(nextTier || currentTier);
                        const currentPoints = stats.points || 0;
                        const basePoints = currentTier ? currentTier.minPoints : 0;
                        const targetPoints = nextTier ? nextTier.minPoints : currentTier.minPoints;
                        // Progress logic: from current level base to next level target
                        const totalNeeded = targetPoints - basePoints;
                        const earnedInLevel = Math.max(0, currentPoints - basePoints);
                        const progressValue = nextTier ? Math.min((earnedInLevel / (totalNeeded || 1)) * 100, 100) : 100;
                        const remainingPoints = Math.max(0, targetPoints - currentPoints);

                        return (
                            <div className="card glass-premium" style={{ padding: '1.5rem', background: 'linear-gradient(135deg, var(--bg-card) 0%, ' + primaryColor + '08 100%)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
                                    <div>
                                        <div style={{ fontSize: '0.7rem', fontWeight: 800, color: currentTier?.color || primaryColor, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                                            Güncel Seviye
                                        </div>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: 900, margin: 0 }}>
                                            {currentTier?.label || 'Üye'}
                                        </h3>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '0.7rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                                            {nextTier ? `HEDEF: ${nextTier.label}` : 'ZİRVEDESİN!'}
                                        </div>
                                        <span style={{ fontSize: '1.25rem', fontWeight: 900, color: currentTier?.color || primaryColor }}>{currentPoints}</span>
                                        <span style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-muted)' }}> / {targetPoints}</span>
                                    </div>
                                </div>

                                <div style={{ position: 'relative', height: '12px', background: 'var(--bg-main)', borderRadius: '10px', overflow: 'hidden', border: '1px solid var(--border)' }}>
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        height: '100%',
                                        width: progressValue + '%',
                                        background: currentTier?.color || primaryColor,
                                        borderRadius: '10px',
                                        transition: 'width 1s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                        boxShadow: '0 0 15px ' + (currentTier?.color || primaryColor) + '40'
                                    }} />
                                </div>

                                {nextTier && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '1rem', padding: '10px', background: 'white', borderRadius: '12px', border: '1px solid ' + primaryColor + '20' }}>
                                        <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: nextTier.color || primaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                                            <Star size={16} fill="white" />
                                        </div>
                                        <p style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-muted)', margin: 0 }}>
                                            <span style={{ color: primaryColor, fontWeight: 800 }}>{remainingPoints} puan</span> daha kazanarak <span style={{ color: 'var(--text-main)', fontWeight: 800 }}>{nextTier.label}</span> rütbesine yükselebilirsin!
                                        </p>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Loyalty QR Code Card */}
                    <div className="card glass-premium" style={{ padding: '1.5rem', textAlign: 'center', border: '2px dashed ' + primaryColor + '40' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '1rem', color: primaryColor }}>
                            <QrCode size={20} />
                            <span style={{ fontWeight: 800, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                {t('customers.loyaltyCard')}
                            </span>
                        </div>

                        <div style={{
                            background: '#ffffff',
                            padding: '1.25rem',
                            borderRadius: '20px',
                            display: 'inline-block',
                            marginBottom: '1rem',
                            boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
                            border: '1px solid var(--border)'
                        }}>
                            <QRCodeSVG value={customer.shortId || customerId} size={180} level="H" includeMargin={true} />
                        </div>

                        <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '1rem', fontWeight: 500, margin: '0 0 1rem 0' }}>
                            {t('customers.scanToAward')}
                        </p>

                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            padding: '8px 12px',
                            background: 'var(--bg-main)',
                            borderRadius: '10px',
                            fontSize: '0.75rem',
                            color: 'var(--text-muted)',
                            border: '1px solid var(--border)',
                            width: 'fit-content',
                            margin: '0 auto'
                        }}>
                            <Hash size={14} />
                            <span style={{ fontWeight: 700, fontFamily: 'monospace' }}>{customer.shortId || customerId}</span>
                        </div>
                    </div>

                    {/* Recent History */}
                    <div className="card" style={{ padding: '1.5rem' }}>
                        <HistoryTabs
                            history={history}
                            customer={customer}
                            discounts={discounts}
                            primaryColor={primaryColor}
                            tenant={tenant}
                            t={t}
                        />
                    </div>
                </div>
            ) : (
                <div className="card glass-premium" style={{ padding: '2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '2rem' }}>
                        <button
                            onClick={() => setIsEditingProfile(false)}
                            style={{ background: 'var(--bg-main)', border: 'none', padding: '8px', borderRadius: '10px', color: 'var(--text-muted)', cursor: 'pointer' }}
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <div>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0 }}>Profil Bilgileri</h3>
                            <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>Bilgilerinizi buradan güncelleyebilirsiniz.</p>
                        </div>
                    </div>

                    <form onSubmit={async (e) => {
                        await handleUpdateProfile(e);
                        setIsEditingProfile(false);
                    }} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '0.75rem', fontWeight: 700, opacity: 0.7 }}>Ad</label>
                                <div style={{ position: 'relative' }}>
                                    <User size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', opacity: 0.4 }} />
                                    <input
                                        type="text"
                                        value={customer.firstName || ''}
                                        onChange={(e) => setCustomer(prev => ({ ...prev, firstName: e.target.value }))}
                                        placeholder="Ad"
                                        style={{ width: '100%', padding: '12px 12px 12px 40px', background: 'var(--bg-main)', border: '1px solid var(--border)', borderRadius: '12px', fontSize: '0.875rem', color: 'inherit' }}
                                        required
                                    />
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '0.75rem', fontWeight: 700, opacity: 0.7 }}>Soyad</label>
                                <div style={{ position: 'relative' }}>
                                    <User size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', opacity: 0.4 }} />
                                    <input
                                        type="text"
                                        value={customer.lastName || ''}
                                        onChange={(e) => setCustomer(prev => ({ ...prev, lastName: e.target.value }))}
                                        placeholder="Soyad"
                                        style={{ width: '100%', padding: '12px 12px 12px 40px', background: 'var(--bg-main)', border: '1px solid var(--border)', borderRadius: '12px', fontSize: '0.875rem', color: 'inherit' }}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label style={{ fontSize: '0.75rem', fontWeight: 700, opacity: 0.7 }}>E-posta</label>
                            <div style={{ position: 'relative' }}>
                                <Mail size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', opacity: 0.4 }} />
                                <input
                                    type="email"
                                    value={customer.email || ''}
                                    onChange={(e) => setCustomer(prev => ({ ...prev, email: e.target.value }))}
                                    placeholder="E-posta"
                                    style={{ width: '100%', padding: '12px 12px 12px 40px', background: 'var(--bg-main)', border: '1px solid var(--border)', borderRadius: '12px', fontSize: '0.875rem', color: 'inherit' }}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label style={{ fontSize: '0.75rem', fontWeight: 700, opacity: 0.7 }}>Telefon</label>
                            <div style={{ position: 'relative' }}>
                                <Phone size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', opacity: 0.4 }} />
                                <input
                                    type="tel"
                                    value={customer.phone || ''}
                                    onChange={(e) => setCustomer(prev => ({ ...prev, phone: e.target.value }))}
                                    placeholder="Telefon"
                                    style={{ width: '100%', padding: '12px 12px 12px 40px', background: 'var(--bg-main)', border: '1px solid var(--border)', borderRadius: '12px', fontSize: '0.875rem', color: 'inherit' }}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label style={{ fontSize: '0.75rem', fontWeight: 700, opacity: 0.7 }}>Doğum Tarihi</label>
                            <PremiumDatePicker
                                value={customer.birthday || ''}
                                onChange={(val) => setCustomer(prev => ({ ...prev, birthday: val }))}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={paymentLoading}
                            style={{
                                marginTop: '1rem',
                                padding: '16px',
                                background: primaryColor,
                                color: 'var(--text-on-primary)',
                                border: 'none',
                                borderRadius: '16px',
                                fontWeight: 800,
                                fontSize: '1rem',
                                cursor: paymentLoading ? 'not-allowed' : 'pointer',
                                boxShadow: `0 10px 20px -5px ${primaryColor}40`,
                                opacity: paymentLoading ? 0.7 : 1
                            }}
                        >
                            {paymentLoading ? 'Kaydediliyor...' : 'Bilgilerimi Güncelle'}
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default CustomerPortalProfile;
